// DOM-free helpers for the filter chips controller, kept separate so they can be unit tested in node.

const MAX_LISTED_VALUES = 2

// Sorting, paging and tabs are not filters; the discard filter only counts once it differs from "active only".
export function isFilterParam(name) {
  return (name.startsWith("ransack_query[") && name !== "ransack_query[s]") || name.startsWith("discard_state_in")
}

// Groups the applied filter params into one chip per form field. describe(name) returns
// { key, label, display } for the field that submits that param, or null when the form has no such field.
export function buildChips(params, describe) {
  const chips = new Map()

  for (const [name, value] of params) {
    if (!isFilterParam(name) || value === "") continue

    const field = describe(name)
    const key = field?.key ?? name
    const chip = chips.get(key) ?? { label: field?.label || humanize(name), display: field?.display, names: [], values: [] }
    if (!chip.names.includes(name)) chip.names.push(name)
    chip.values.push(value)
    chips.set(key, chip)
  }

  return [...chips.values()]
    .filter(chip => !(chip.names.every(name => name.startsWith("discard_state_in")) && chip.values.join() === "kept"))
    .map(({ label, display, names, values }) => ({ label, names, text: display ?? summarize(values) }))
}

// The two ends of a from/to group, either of which may be empty.
export function rangeText(from, to) {
  if (from && to) return `${from} → ${to}`
  return from ? `≥ ${from}` : `≤ ${to}`
}

export function summarize(values) {
  const listed = values.slice(0, MAX_LISTED_VALUES).join(", ")
  const rest = values.length - MAX_LISTED_VALUES
  return rest > 0 ? `${listed} +${rest}` : listed
}

// "ransack_query[company_cont]" -> "Company"
export function humanize(name) {
  const attribute = (name.match(/\[([^\]]+)\]/)?.[1] ?? name)
    .replace(/_(cont|eq|in|not_in|gteq|lteq|null|start)$/, "")
    .replace(/_/g, " ")
  return attribute.charAt(0).toUpperCase() + attribute.slice(1)
}

// The current URL without the given params, back on the first page.
export function urlWithout(href, names) {
  const url = new URL(href)
  for (const name of [...names, "page"]) url.searchParams.delete(name)
  return url.toString()
}
